import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { Clock, ArrowRight, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import TimeTrackLogo from "../components/TimeTrackLogo";

export default function Signup() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    company_name: "",
    name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Pre-fill from ?email=&company_name= (from landing page trial CTA)
  useEffect(() => {
    const prefEmail = searchParams.get("email") || "";
    const prefCompany = searchParams.get("company_name") || "";
    if (prefEmail || prefCompany) {
      setForm((prev) => ({
        ...prev,
        email: prefEmail || prev.email,
        company_name: prefCompany || prev.company_name,
      }));
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/signup", form);
      localStorage.setItem("lom_token", data.access_token);
      setUser(data.user);
      toast.success(`Welcome ${data.user.name}!`);
      nav("/home");
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-zinc-50">
      <div className="relative hidden md:flex flex-col justify-between p-12 bg-zinc-950 text-white overflow-hidden">
        <div className="relative z-10">
          <TimeTrackLogo size={44} variant="lockup" invert />
        </div>
        <div className="relative z-10 space-y-6">
          <div className="label-eyebrow text-zinc-400">Start in 60 seconds</div>
          <h1 className="font-heading font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
            Track your team.<br />
            <span className="text-emerald-400">Free forever</span> for<br />
            up to 5 staff.
          </h1>
          <p className="text-zinc-400 max-w-md leading-relaxed">
            GPS-verified clock in/out, late detection, multi-location dashboards.
            No credit card. Cancel anytime.
          </p>
        </div>
        <div className="relative z-10 space-y-2 border-t border-zinc-800 pt-6">
          <Feature text="Click-to-place store on map" />
          <Feature text="Geofence radius up to 10 miles" />
          <Feature text="Store-network IP verification" />
          <Feature text="Per-store timezone support" />
        </div>
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, white 1px, transparent 1px), linear-gradient(to bottom, white 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="label-eyebrow mb-2">Create your company</div>
          <h2 className="font-heading font-black text-4xl tracking-tighter text-zinc-950 mb-2">
            Sign up
          </h2>
          <p className="text-zinc-600 mb-8">
            Your account becomes the admin. You can invite your team next.
            <span className="block text-xs text-zinc-500 mt-2">
              LOM TimeTrack is a <a href="https://lomgroupja.com" target="_blank" rel="noreferrer" className="underline">LOM Solutions Jamaica</a> product.
            </span>
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="label-eyebrow block mb-2">Company name</label>
              <input
                data-testid="signup-company-input"
                type="text"
                required
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors text-sm"
                placeholder="Acme Retail Inc."
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Your name</label>
              <input
                data-testid="signup-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors text-sm"
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Work email</label>
              <input
                data-testid="signup-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors font-mono text-sm"
                placeholder="jane@acme.com"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                data-testid="signup-password-input"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 bg-white border border-zinc-300 rounded-sm focus:outline-none focus:border-zinc-950 transition-colors font-mono text-sm"
                placeholder="At least 8 characters"
              />
            </div>

            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm" data-testid="signup-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="signup-submit-btn"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3.5 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-50 text-white font-semibold rounded-sm transition-colors group"
            >
              <Buildings size={16} weight="bold" />
              <span>{loading ? "Creating company…" : "Create company"}</span>
              <ArrowRight weight="bold" size={16} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-zinc-600">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-zinc-950 underline" data-testid="signup-to-login-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Feature({ text }) {
  return (
    <div className="flex items-center gap-2 text-sm text-zinc-300">
      <span className="w-1 h-1 bg-emerald-400 rounded-full" />
      {text}
    </div>
  );
}
